/**
 * Utilities for rendering HTML file previews via iframe `srcDoc`.
 *
 * `prepareSrcDocHtml` injects force-light declarations (and optional island
 * edit styles + contenteditable) before the document is loaded. On first load
 * callers set the returned string as `srcDoc`. On subsequent content changes,
 * use `morphPreparedHtmlIntoDocument` instead of replacing `srcDoc`.
 * `computeHtmlSrcDocFingerprint` gates when an update is needed.
 *
 * Two families of options serve two surfaces with different sandbox postures,
 * and they are mutually exclusive by design:
 *
 * - The detail pane renders same-origin (`allow-same-origin`), so the parent
 *   reaches into the document directly. It uses `editable` (parse-time
 *   `contenteditable` on islands + a navigation guard) and the
 *   `customCss`/`customJavaScript` injection seam.
 * - Canvas blocks render opaque-origin (`allow-scripts` only), so the parent
 *   can never touch the DOM. They use `blockRuntime` (an injected postMessage
 *   runtime the parent drives with `config` messages) plus `colorScheme`,
 *   `extraHeadCss`, `injectFontLinks` and `transparentBackground`.
 */

import {
  createCohesiveBlockFontLinks,
  getCohesiveBlockTokensCss,
} from "./cohesiveBlockTokens";
import {
  BLOCK_RUNTIME_SCRIPT,
  injectIslandAttr,
  type BlockStateViewer,
} from "./htmlBlockRuntime";

const FNV_OFFSET_BASIS_32 = 0x811c9dc5;
const FNV_PRIME_32 = 16777619;

function fnv1a32(input: string): string {
  let hash = FNV_OFFSET_BASIS_32;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, FNV_PRIME_32) >>> 0;
  }
  return (hash >>> 0).toString(36);
}

/**
 * CSS injected into the iframe to highlight editable islands during an edit
 * session. Uses `outline` (not border) so it doesn't shift layout.
 *
 * Hover is a background tint, not an outline: an outline needs
 * `outline-offset` to clear the text, which pushes it into whatever sits next
 * to it (adjacent islands' outlines collide on a dense document), and a 2px
 * stroke composited at an arbitrary canvas zoom lands on fractional pixels
 * and renders soft. A tint paints inside the element's own box. The crisp
 * outline is kept for `:focus`, marking the one island holding the caret —
 * and the tint drops there, so an island shows the hover tint OR the focus
 * box, never both stacked.
 *
 * Transitions sit on the state rules, never the base rule: this CSS starts
 * applying the instant an edit session opens, and a base-rule transition
 * would animate `outline-color` from its initial `currentColor` to
 * `transparent` — a text-colored outline flashing around every island on
 * entry.
 */
export const ISLAND_EDIT_CSS = `
  [data-edit-id]:not([data-no-edit]) {
    outline: 2px solid transparent;
    outline-offset: 2px;
    cursor: text;
  }
  [data-edit-id]:not([data-no-edit]):hover {
    background-color: rgba(99, 102, 241, 0.09);
    transition: background-color 0.15s;
  }
  [data-edit-id]:not([data-no-edit]):focus,
  [data-edit-id]:not([data-no-edit]):focus:hover {
    outline-color: rgb(99, 102, 241);
    background-color: transparent;
    transition: outline-color 0.15s;
  }
  [data-edit-id]:not([data-no-edit]) a,
  [data-edit-id]:not([data-no-edit]) button {
    cursor: text;
  }
`;

/**
 * Runs inside the iframe on load when `editable` is true. Blocks navigation
 * and control activation inside editable islands while still allowing the
 * contenteditable caret to be placed in link/button text.
 */
const EDITABLE_NAVIGATION_GUARD_SCRIPT = `
(function () {
  function editableIsland(node) {
    return node && node.closest && node.closest('[data-edit-id]:not([data-no-edit])');
  }

  // Prevent buttons and form controls from stealing focus on mousedown.
  // Links are intentionally excluded — blocking mousedown there prevents
  // placing the caret inside link text; navigation is blocked on click.
  document.addEventListener('mousedown', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (!editableIsland(t)) return;
    if (t.closest('button, input, select, textarea')) {
      e.preventDefault();
    }
  }, true);

  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;

    if (editableIsland(t)) {
      if (t.closest('a, button, input, select, textarea')) {
        e.preventDefault();
      }
      return;
    }

    var a = t.closest('a');
    if (!a) return;
    e.preventDefault();
    var href = a.getAttribute('href');
    if (!href || href === '#') return;
    if (href.charAt(0) === '#') {
      var target = document.getElementById(href.slice(1));
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    window.open(href, '_blank', 'noopener,noreferrer');
  }, true);

  document.querySelectorAll('form').forEach(function (form) {
    form.addEventListener('submit', function (e) { e.preventDefault(); });
  });
})();
`;

const FORCE_LIGHT_HEAD =
  '<meta name="color-scheme" content="light" data-cohesive-injected>' +
  "<style data-cohesive-force-light data-cohesive-injected>:root{color-scheme:light !important;}</style>";

/**
 * Island edit CSS, scoped under the `data-cohesive-editing` root attribute
 * that the block runtime toggles when the parent starts/ends an edit session.
 * This lets the CSS ship in every block srcDoc while staying invisible
 * outside sessions — the block card never re-prepares its document to change
 * mode, because touching `srcdoc` would reload the iframe.
 */
const BLOCK_ISLAND_EDIT_CSS = ISLAND_EDIT_CSS.split(
  "[data-edit-id]:not([data-no-edit])",
).join("html[data-cohesive-editing] [data-edit-id]:not([data-no-edit])");

/**
 * The island under a hover: at rest, tagged by the runtime from the host's
 * forwarded pointer (the shield keeps in-frame :hover from ever firing on
 * content); in interact-only mode, native :hover under the root marker the
 * runtime sets. Both get the same tint an edit session uses, so "this text
 * is editable" reads identically however the block was entered. Controls
 * need no injected style: over a control the host steps its shield aside
 * and the control's own hover state shows natively.
 */
const BLOCK_HOVER_CSS = `
  [data-cohesive-hot][data-edit-id]:not([data-no-edit]),
  html[data-cohesive-interacting] [data-edit-id]:not([data-no-edit]):hover {
    background-color: rgba(99, 102, 241, 0.09);
    cursor: text;
    transition: background-color 0.15s;
  }
`;

/** Marker attribute for caller-provided CSS/JS injection nodes. */
export const COHESIVE_CUSTOM_ATTR = "data-cohesive-custom";

export interface PrepareSrcDocHtmlOptions {
  /** When true, inject island edit CSS and set contenteditable on islands. */
  editable?: boolean;
  /**
   * Trusted CSS injected into a `style[data-cohesive-custom]` node in `<head>`.
   * Callers must only pass application-controlled stylesheets.
   */
  customCss?: string;
  /**
   * Trusted JavaScript placed in a `script[data-cohesive-custom]` node at the
   * end of `<body>` so `document.body` exists when it runs. Executes on initial
   * `srcDoc` load; callers that morph should re-run it via
   * {@link applyCustomJavaScriptInDocument} afterward.
   */
  customJavaScript?: string;
  /**
   * HTML-block variant: inject this explicit color scheme instead of the
   * legacy force-light declarations. Must match the embedding element's
   * `colorScheme` — on a mismatch the browser paints an opaque backstop
   * behind the frame, which silently breaks transparent blocks. `"dark"` also
   * sets `class="dark"` on the document root, which is what activates the
   * injected token stylesheet's `.dark` overrides (the same mechanism the app
   * itself uses). Omit for the legacy force-light behavior.
   */
  colorScheme?: "light" | "dark";
  /** Force `html, body { background: transparent }` so the canvas shows
   *  through (presentation.transparent blocks). */
  transparentBackground?: boolean;
  /** Force `html, body { overflow: hidden }` so the document never becomes
   *  a scroller. An inert frame (no pointer events) loses nothing, and in
   *  WebKit a scrollable sub-frame document promotes the whole canvas
   *  viewport to a composited layer, which rasterises every node at device
   *  scale and blurs text at any zoom but 1. */
  lockScroll?: boolean;
  /** Stylesheet text prepended to `<head>` — before the document's own
   *  styles, so agent CSS can override it. Used for the Cohesive token vars. */
  extraHeadCss?: string;
  /** Inject the app's Google Fonts links (DM Sans, Source Serif 4) so block
   *  text can resolve the Cohesive font stacks. */
  injectFontLinks?: boolean;
  /**
   * Inject the cohesive-block postMessage runtime plus the session-scoped
   * island edit CSS. One srcDoc then serves display, interactive and edit
   * modes — the parent flips modes with config messages instead of
   * re-preparing, which is what keeps in-frame JS state alive across a
   * toggle. No parse-time `contenteditable` is set; the runtime applies it
   * when the parent's config says so.
   *
   * Mutually exclusive with {@link PrepareSrcDocHtmlOptions.editable}: they
   * are two implementations of the same concern for two sandbox postures, and
   * combining them would double-bind island handlers.
   */
  blockRuntime?: boolean;
  /**
   * What the runtime knows before the host speaks: who is looking and
   * whether state is writable, so `cohesive.viewer` is set when the
   * document's own scripts first run. Requires {@link blockRuntime}.
   */
  blockBoot?: BlockRuntimeBoot;
}

export interface BlockRuntimeBoot {
  viewer: BlockStateViewer | null;
  readonly: boolean;
}

export const BLOCK_BOOT_ELEMENT_ID = "cohesive-block-boot";

/**
 * Upsert or remove the custom CSS `<style>` node in a live iframe document.
 *
 * Owns every `style[data-cohesive-custom]` node: head merges during morphs
 * protect cohesive-injected nodes from removal while also appending the
 * prepared copy, so superseded duplicates accumulate — and a stale duplicate
 * later in the head wins the cascade. Collapse to exactly one node here,
 * updating in place so there is no flash of unstyled content.
 */
export function applyCustomCssInDocument(
  doc: Document,
  css: string | undefined,
): void {
  const existing = Array.from(
    doc.querySelectorAll<HTMLStyleElement>(`style[${COHESIVE_CUSTOM_ATTR}]`),
  );
  if (!css) {
    for (const node of existing) node.remove();
    return;
  }
  const [first, ...rest] = existing;
  for (const node of rest) node.remove();
  if (first) {
    first.textContent = css;
    return;
  }
  const style = doc.createElement("style");
  style.setAttribute(COHESIVE_CUSTOM_ATTR, "");
  style.setAttribute("data-cohesive-injected", "");
  style.textContent = css;
  (doc.head ?? doc.documentElement).appendChild(style);
}

/**
 * Remove any prior custom script nodes and append a fresh one so the browser
 * executes `code`. Scripts must be idempotent — this runs after every morph.
 *
 * Appended to `<body>` when present so the script can touch body content.
 */
export function applyCustomJavaScriptInDocument(
  doc: Document,
  code: string | undefined,
): void {
  doc
    .querySelectorAll(`script[${COHESIVE_CUSTOM_ATTR}]`)
    .forEach((node) => node.remove());
  if (!code) return;
  const script = doc.createElement("script");
  script.setAttribute(COHESIVE_CUSTOM_ATTR, "");
  script.setAttribute("data-cohesive-injected", "");
  script.textContent = code;
  (doc.body ?? doc.head ?? doc.documentElement).appendChild(script);
}

/**
 * The block runtime and the form guard it relies on, first in `head`. The
 * file viewer injects the same pair into published blocks.
 */
export function injectBlockRuntime(
  head: HTMLElement,
  blockBoot?: BlockRuntimeBoot,
) {
  const doc = head.ownerDocument;
  const runtime = doc.createElement("script");
  runtime.setAttribute("data-cohesive-block-runtime", "");
  runtime.setAttribute("data-cohesive-injected", "");
  runtime.setAttribute("im-preserve", "true");
  runtime.textContent = BLOCK_RUNTIME_SCRIPT;
  // First in <head>, ahead of the font links and every stylesheet the
  // document itself carries: a parser-inserted script waits for all earlier
  // pending stylesheets, so anything before it that stalls would keep the
  // runtime from booting — and its `ready` message is one of the two
  // signals that reveal the block. The runtime only registers listeners and
  // announces itself on DOMContentLoaded, so it needs nothing before it.
  head.insertBefore(runtime, head.firstChild);
  if (blockBoot) {
    const boot = doc.createElement("script");
    boot.type = "application/json";
    boot.id = BLOCK_BOOT_ELEMENT_ID;
    boot.setAttribute("data-cohesive-injected", "");
    boot.textContent = JSON.stringify(blockBoot).replace(
      /[<>&]/g,
      (c) => `\\u${c.charCodeAt(0).toString(16).padStart(4, "0")}`,
    );
    head.insertBefore(boot, runtime);
  }

  // The frame allows forms so a submit event fires and the block's handler
  // runs; the runtime cancels every submit, and this refuses any that slips
  // past, so a form can never navigate the frame. First, ahead of the
  // markup it governs.
  const formGuard = doc.createElement("meta");
  formGuard.setAttribute("http-equiv", "Content-Security-Policy");
  formGuard.setAttribute("content", "form-action 'none'");
  formGuard.setAttribute("data-cohesive-injected", "");
  formGuard.setAttribute("im-preserve", "true");
  head.insertBefore(formGuard, head.firstChild);
}

/**
 * Parse `html`, inject head content for iframe rendering, and serialize for
 * `srcDoc`. Defaults to the legacy force-light preview treatment; pass
 * `colorScheme` (plus token CSS, font links, transparency) for theme-matched
 * block documents. Editing is either parse-time (`editable`, same-origin
 * panes) or runtime-driven (`blockRuntime`, opaque-origin canvas blocks).
 */
export function prepareSrcDocHtml(
  html: string,
  options: PrepareSrcDocHtmlOptions = {},
): string {
  const {
    editable = false,
    customCss,
    customJavaScript,
    colorScheme,
    transparentBackground = false,
    lockScroll = false,
    extraHeadCss,
    injectFontLinks = false,
    blockRuntime = false,
    blockBoot,
  } = options;

  if (editable && blockRuntime) {
    throw new Error(
      "prepareSrcDocHtml: `editable` and `blockRuntime` are mutually exclusive — " +
        "parse-time island editing is for same-origin panes, the runtime is for " +
        "opaque-origin canvas blocks.",
    );
  }

  const doc = new DOMParser().parseFromString(html, "text/html");

  let head = doc.head;
  if (!head) {
    head = doc.createElement("head");
    doc.documentElement.insertBefore(head, doc.body);
  }

  // Stored agent HTML may carry backend-baked tokens marked
  // `data-cohesive-tokens` — an inline `<style>` on blocks (current), or a
  // `<link>` to the hosted stylesheet on older records — so surfaces that
  // render the raw bytes (preview lambda, public shares) match the canvas.
  // In-app we inject the current CSS ourselves — drop the stored copy so it
  // doesn't duplicate and theme control stays local.
  if (extraHeadCss) {
    doc
      .querySelectorAll(
        "link[data-cohesive-tokens], style[data-cohesive-tokens]",
      )
      .forEach((el) => el.remove());
  }

  // Prepended content loses ties against the document's own rules, so agent
  // CSS can deliberately override the injected tokens and fonts.
  if (injectFontLinks || extraHeadCss) {
    const prependFragment = doc.createDocumentFragment();
    if (injectFontLinks) {
      prependFragment.appendChild(createCohesiveBlockFontLinks(doc));
    }
    if (extraHeadCss) {
      const tokensStyle = doc.createElement("style");
      tokensStyle.setAttribute("data-cohesive-tokens", "");
      tokensStyle.setAttribute("data-cohesive-injected", "");
      tokensStyle.textContent = extraHeadCss;
      prependFragment.appendChild(tokensStyle);
    }
    head.insertBefore(prependFragment, head.firstChild);
  }

  // Appended content wins ties against the document's rules — the color
  // scheme in particular must hold for the iframe to composite correctly.
  const headFragment = doc.createElement("template");
  if (colorScheme) {
    headFragment.innerHTML =
      `<meta name="color-scheme" content="${colorScheme}" data-cohesive-injected>` +
      `<style data-cohesive-color-scheme data-cohesive-injected>:root{color-scheme:${colorScheme};}</style>`;
    // The token stylesheet carries its own `.dark` overrides; the class on
    // the root is what selects them (mirrors the app's theme mechanism).
    if (colorScheme === "dark") {
      doc.documentElement.classList.add("dark");
    } else {
      doc.documentElement.classList.remove("dark");
    }
  } else {
    headFragment.innerHTML = FORCE_LIGHT_HEAD;
  }
  if (transparentBackground) {
    const transparencyStyle = doc.createElement("style");
    transparencyStyle.setAttribute("data-cohesive-transparent", "");
    transparencyStyle.setAttribute("data-cohesive-injected", "");
    transparencyStyle.textContent =
      "html, body { background: transparent !important; }";
    headFragment.content.appendChild(transparencyStyle);
  }
  if (lockScroll) {
    const scrollLockStyle = doc.createElement("style");
    scrollLockStyle.setAttribute("data-cohesive-scroll-lock", "");
    scrollLockStyle.setAttribute("data-cohesive-injected", "");
    scrollLockStyle.textContent = "html, body { overflow: hidden !important; }";
    headFragment.content.appendChild(scrollLockStyle);
  }
  if (blockRuntime) {
    const blockEditStyle = doc.createElement("style");
    blockEditStyle.setAttribute("data-cohesive-edit-scoped", "");
    blockEditStyle.setAttribute("data-cohesive-injected", "");
    blockEditStyle.textContent = BLOCK_ISLAND_EDIT_CSS + BLOCK_HOVER_CSS;
    headFragment.content.appendChild(blockEditStyle);
  }
  if (editable) {
    const style = doc.createElement("style");
    style.setAttribute("data-cohesive-edit", "");
    style.setAttribute("data-cohesive-injected", "");
    style.textContent = ISLAND_EDIT_CSS;
    headFragment.content.appendChild(style);
  }
  if (customCss) {
    const customStyle = doc.createElement("style");
    customStyle.setAttribute(COHESIVE_CUSTOM_ATTR, "");
    customStyle.setAttribute("data-cohesive-injected", "");
    customStyle.textContent = customCss;
    headFragment.content.appendChild(customStyle);
  }
  head.appendChild(headFragment.content);

  if (editable) {
    doc
      .querySelectorAll<HTMLElement>("[data-edit-id]:not([data-no-edit])")
      .forEach((el) => {
        el.setAttribute("contenteditable", "true");
        el.setAttribute("spellcheck", "false");

        const editId = el.getAttribute("data-edit-id");
        if (editId && !el.id) {
          el.id = editId;
        }

        // Marker-recorded (`data-cohesive-was-*`), matching the block
        // runtime's injectAttr: island reads strip this scaffolding via
        // `readIslandInnerHtml`, so a region PATCH never persists it into the
        // stored document.
        el.querySelectorAll("button").forEach((button) => {
          injectIslandAttr(button, "tabindex", "-1");
          if (!button.getAttribute("type")) {
            injectIslandAttr(button, "type", "button");
          }
        });
      });

    const navGuard = doc.createElement("script");
    navGuard.setAttribute("data-cohesive-nav-guard", "");
    navGuard.setAttribute("data-cohesive-injected", "");
    navGuard.setAttribute("im-preserve", "true");
    navGuard.textContent = EDITABLE_NAVIGATION_GUARD_SCRIPT;
    head.appendChild(navGuard);
  }

  if (blockRuntime) injectBlockRuntime(head, blockBoot);

  if (customJavaScript) {
    const customScript = doc.createElement("script");
    customScript.setAttribute(COHESIVE_CUSTOM_ATTR, "");
    customScript.setAttribute("data-cohesive-injected", "");
    customScript.textContent = customJavaScript;
    // End of body, not head: head scripts run before the body is parsed, so
    // anything touching `document.body` would throw on the initial load.
    (doc.body ?? head).appendChild(customScript);
  }

  return `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;
}

export interface BlockPrepareOptionsParams {
  /** App color scheme the embedding element is using. */
  theme: "light" | "dark";
  /** presentation.transparent — composite the block onto the canvas. */
  transparent: boolean;
  boot: BlockRuntimeBoot;
}

/**
 * The canonical `prepareSrcDocHtml` options for a canvas HTML block. Every
 * block surface builds its options here so they cannot drift.
 */
export function buildBlockPrepareOptions({
  theme,
  transparent,
  boot,
}: BlockPrepareOptionsParams): PrepareSrcDocHtmlOptions {
  return {
    colorScheme: theme,
    transparentBackground: transparent,
    extraHeadCss: getCohesiveBlockTokensCss(),
    // The block asks the host for its webfonts over the bridge
    // (blockFonts.ts) — no per-frame network.
    injectFontLinks: false,
    blockRuntime: true,
    blockBoot: boot,
  };
}

/**
 * Fingerprint salt for the options {@link buildBlockPrepareOptions} produces.
 *
 * The content fingerprint hashes the document body alone, so it cannot see
 * that a prepare option changed the *prepared* output. Folding this salt in
 * makes such a change register as new content while presigned-URL rotation
 * still doesn't. The theme is deliberately NOT part of it: the initial
 * document is prepared for the theme of the moment, and later flips are
 * applied in place by the runtime (config `theme`) — a remount per theme
 * flip is exactly what this avoids.
 */
export function blockPrepareOptionsSalt(
  transparent: boolean,
  boot: BlockRuntimeBoot,
): string {
  return `:block:${transparent ? "t" : "o"}:${boot.viewer?.id ?? "-"}:${boot.readonly ? "r" : "w"}`;
}

/**
 * Stable fingerprint for iframe remounting. Excludes presigned URL tokens so
 * URL rotation does not reload the iframe. `contentHash` is preferred when
 * available (server-side content identity); falls back to FNV-1a of `body`.
 */
export function computeHtmlSrcDocFingerprint(
  body: string,
  contentHash: string | undefined,
  missingPresignIds?: Iterable<string>,
): string {
  const bodyKey =
    contentHash && contentHash.length > 0 ? contentHash : fnv1a32(body);
  const missingKey = missingPresignIds
    ? [...missingPresignIds].sort().join(",")
    : "";
  return `${bodyKey}:${missingKey}`;
}
